import React, { useEffect, useRef, useState } from 'react';
import I18n from 'i18n-js';
import { withSnackbar } from 'notistack';
import classNames from 'classnames';
import eyeson, { DeviceManager, Logger } from '@eyeson/js';
import {
  Button,
  Typography,
  Select,
  MenuItem,
  Slider,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import GenericButton from './generic/Button.js';
import Video from './streams/Video.js';
import { makeStyles, withStyles } from '@material-ui/styles';
import { red } from '@material-ui/core/colors';
import ReloadIcon from '@material-ui/icons/Autorenew';
import CloseIcon from '@material-ui/icons/Close';
import SwitchVideoIcon from '@material-ui/icons/SwitchVideo';
import DarkIcon from '@material-ui/icons/Brightness3';
import LightIcon from '@material-ui/icons/Brightness7';
import ActivityMonitor from '../utils/ActivityMonitor.js';
import DialogWindow from './dialog/DialogWindow.js';
import InactiveDialog from './dialog/InactiveDialog.js';
import WebViewDetection from '../utils/WebViewDetection.js';
import WarningIcon from '@material-ui/icons/Warning';

const _scope = 'second-cam.';

const useStyles = makeStyles((theme) => ({
  row: {
    marginBlock: theme.spacing(2),
  },
  loading: {
    marginInlineEnd: '0.5rem',
    color: 'currentColor',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  title: {
    marginBlock: theme.spacing(2),
  },
  zoom: {
    display: 'grid',
    gridTemplateColumns: '1fr 2rem',
    gap: '1rem',
    alignItems: 'center',
    '& .zoom-value': {
      textAlign: 'end',
    },
  },
  optionRight: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '1rem',
    alignItems: 'center',
  },
  warningIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.warning.main,
    verticalAlign: 'text-bottom',
  },
}));

const StyledSlider = withStyles((theme) => ({
  root: {
    color:
      theme.palette.type === 'light'
        ? theme.palette.primary.dark
        : theme.palette.common.white,
  },
  track: {
    backgroundColor: theme.palette.secondary[theme.palette.type],
  },
  thumb: {
    backgroundColor: theme.palette.secondary[theme.palette.type],
    '&:hover': {
      boxShadow:
        theme.palette.type === 'light'
          ? '0px 0px 0px 8px rgba(0,0,0,.14)'
          : '0px 0px 0px 8px rgba(255,255,255,.16)',
    },
    '&.Mui-focusVisible': {
      boxShadow:
        theme.palette.type === 'light'
          ? '0px 0px 0px 14px rgba(0,0,0,.14)'
          : '0px 0px 0px 14px rgba(255,255,255,.16)',
    },
  },
  active: {
    '&:hover': {
      boxShadow:
        theme.palette.type === 'light'
          ? '0px 0px 0px 14px rgba(0,0,0,.14)'
          : '0px 0px 0px 14px rgba(255,255,255,.16)',
    },
  },
}))(Slider);

const StopButton = withStyles({
  root: {
    color: red[500],
    borderColor: red[500],
    '&:hover': {
      color: red[700],
      borderColor: red[700],
    },
  },
})(Button);

const fetchMeetingInfo = async (token, config) => {
  const response = await fetch(
    `${config.env.REACT_APP_COM_API}/rooms/${token}`,
    { cache: 'no-store' }
  );
  if (!response.ok) {
    throw new Error('failed');
  }
  const data = await response.json();
  return {
    name: data.user.name,
    userId: data.user.id,
    options: data.options,
    guest_token: data.room.guest_token,
  };
};

const registerGuest = async (token, config, params) => {
  const response = await fetch(
    `${config.env.REACT_APP_COM_API}/guests/${token}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify(params),
    }
  );
  if (!response.ok) {
    throw new Error('failed');
  }
  const data = await response.json();
  return data;
};

const findActiveCamera = (stream) => {
  if (!stream) {
    return null;
  }
  const tracks = stream.getVideoTracks();
  if (tracks.length === 0) {
    return null;
  }
  const { deviceId } = tracks[0].getSettings();
  return deviceId;
};

const getFacingMode = (stream) => {
  if (stream) {
    const tracks = stream.getVideoTracks();
    if (tracks.length > 0) {
      const [track] = tracks;
      if (typeof track.getSettings === 'function') {
        const settings = track.getSettings();
        if (settings && 'facingMode' in settings) {
          return settings.facingMode;
        }
      }
    }
  }
  return null;
};

const parseLayout = (options) => {
  let layout = null;
  if (options.layout_map) {
    try {
      layout = {
        layout: options.layout,
        name: options.layout_name,
        users: options.layout_users,
        map: JSON.stringify(options.layout_map),
        show_names: options.show_names,
        voice_activation: options.voice_activation,
      };
    } catch (error) {
      layout = {
        layout: 'auto',
        show_names: options.show_names,
        voice_activation: options.voice_activation,
      };
    }
  } else {
    layout = {
      layout: options.layout,
      name: options.layout_name,
      users: options.layout_users,
      show_names: options.show_names,
      voice_activation: options.voice_activation,
    };
  }
  return layout;
};

const checkIsPresentationLayout = (options, userId) => {
  const { layout, layout_name, layout_users } = options;
  const isPresentationLayout =
    layout === 'auto' &&
    layout_name === 'present-upper-6' &&
    layout_users &&
    layout_users.length === 7 &&
    layout_users[0] === eyeson.user.apiId &&
    layout_users[1] === userId;
  return isPresentationLayout;
};

const ExternalCameraPreview = ({ join, showWarning, theme, toggleTheme }) => {
  const _deviceManager = useRef(null);
  const [joining, setJoining] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const [cameraList, setCameraList] = useState([]);
  const [activeCamera, setActiveCamera] = useState('');
  const [zoomValue, setZoomValue] = useState(
    DeviceManager.getStoredZoom(_scope) || 1
  );
  const classes = useStyles();
  const showWarningRef = useRef(showWarning);

  const start = () => {
    if (!_deviceManager.current) {
      return;
    }
    setJoining(true);
    _deviceManager.current.storeConstraints();
    const options = {
      audio: false,
      video: true,
      sendOnly: true,
      scope: _scope,
      zoom: zoomValue > 1,
    };
    join(options);
  };

  const changeCamera = ({ target }) => {
    const cameraId = target.value;
    setVideoStream(null);
    _deviceManager.current.setVideoInput(cameraId);
  };

  const updateZoom = (_event, value) => {
    setZoomValue(value);
    if (value !== zoomValue && _deviceManager.current) {
      _deviceManager.current.setZoom(value);
    }
  };

  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  useEffect(() => {
    if (_deviceManager.current) {
      return;
    }
    let deviceManager = new DeviceManager({
      audio: false,
      video: true,
      scope: _scope,
    });
    const zoom = DeviceManager.getStoredZoom(_scope) || 1;
    deviceManager.setZoom(zoom);
    _deviceManager.current = deviceManager;
    deviceManager.onChange((event) => {
      const { stream, cameras, error } = event;
      if (error) {
        Logger.warn('DeviceManager::change', error);
        showWarningRef.current(`error_${error.name}`);
      }
      if (stream) {
        const id = findActiveCamera(stream);
        setVideoStream(stream);
        if (id) {
          setActiveCamera(id);
        }
      }
      if (cameras) {
        setCameraList(cameras);
      }
    });
    deviceManager.start();
    return () => {
      if (_deviceManager.current) {
        _deviceManager.current.terminate();
        _deviceManager.current = null;
      }
    };
  }, []);

  return (
    <div className="main">
      <header className={classes.optionRight}>
        <Typography variant="h5">{I18n.t('label:preview')}</Typography>
        <IconButton onClick={toggleTheme}>
          {theme === 'light' ? <LightIcon /> : <DarkIcon />}
        </IconButton>
      </header>
      {activeCamera === '' ? (
        <p>{I18n.t('label:loading')}</p>
      ) : (
        <>
          <div className={classNames('video-wrap', { 'no-cam': !videoStream })}>
            <Video stream={videoStream} muted={true} pipEnabled={false} />
          </div>
          <div className={classes.row}>
            <Typography variant="overline" component="div">
              {I18n.t('devices:camera')}
            </Typography>
            <Select
              fullWidth
              value={activeCamera}
              onChange={changeCamera}
              color="secondary"
              disabled={joining}
              SelectDisplayProps={{
                'aria-label': I18n.t('devices:camera'),
              }}
            >
              {cameraList.map(({ deviceId, label }) => {
                return (
                  <MenuItem key={deviceId} value={deviceId}>
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </div>
          <div className={classes.row}>
            <Typography variant="overline" component="div">
              {I18n.t('label:zoom')}
            </Typography>
            <div className={classes.zoom}>
              <StyledSlider
                value={zoomValue}
                min={1}
                max={4}
                step={0.1}
                onChange={updateZoom}
                disabled={joining}
              />
              <span className="zoom-value">{zoomValue}&times;</span>
            </div>
          </div>
        </>
      )}
      <p className="bottom">
        <Button
          fullWidth
          color="secondary"
          variant="contained"
          onClick={start}
          disabled={activeCamera === '' || joining}
        >
          {joining && (
            <CircularProgress size="1rem" className={classes.loading} />
          )}
          {I18n.t('label:join_room')}
        </Button>
      </p>
    </div>
  );
};

const ExternalCameraPreviewMobile = ({
  join,
  showWarning,
  theme,
  toggleTheme,
}) => {
  const _deviceManager = useRef(null);
  const [joining, setJoining] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const [camSwitchAvailable, setCamSwitchAvailable] = useState(true);
  const [facingMode, setFacingMode] = useState('environment');
  const [zoomValue, setZoomValue] = useState(
    DeviceManager.getStoredZoom(_scope) || 1
  );
  const showWarningRef = useRef(showWarning);
  const classes = useStyles();

  const start = () => {
    if (!_deviceManager.current) {
      return;
    }
    setJoining(true);
    _deviceManager.current.storeConstraints();
    const options = {
      audio: false,
      video: { facingMode },
      sendOnly: true,
      scope: _scope,
      zoom: zoomValue > 1,
    };
    join(options);
  };

  const switchCamera = () => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    setVideoStream(null);
    const newOptions = {
      audio: false,
      video: { facingMode: newFacingMode },
      scope: _scope,
    };
    _deviceManager.current.updateWithOptions(newOptions, true);
  };

  const updateZoom = (_event, value) => {
    setZoomValue(value);
    if (value !== zoomValue && _deviceManager.current) {
      _deviceManager.current.setZoom(value);
    }
  };

  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  useEffect(() => {
    if (_deviceManager.current) {
      return;
    }
    let deviceManager = new DeviceManager({
      audio: false,
      video: { facingMode: 'environment' },
      scope: _scope,
    });
    const zoom = DeviceManager.getStoredZoom(_scope) || 1;
    deviceManager.setZoom(zoom);
    _deviceManager.current = deviceManager;
    deviceManager.onChange((event) => {
      const { stream, cameras, error } = event;
      if (error) {
        Logger.warn('DeviceManager::change', error);
        showWarningRef.current(`error_${error.name}`);
      }
      if (stream) {
        const streamFacingMode = getFacingMode(stream);
        if (streamFacingMode) {
          setFacingMode(streamFacingMode);
        }
        setVideoStream(stream);
      }
      if (cameras) {
        setCamSwitchAvailable(cameras.length > 1);
      }
    });
    deviceManager.start();
    return () => {
      if (_deviceManager.current) {
        _deviceManager.current.terminate();
        _deviceManager.current = null;
      }
    };
  }, []);

  return (
    <div className="main">
      <header className={classes.optionRight}>
        <Typography variant="h5">{I18n.t('label:preview')}</Typography>
        <IconButton onClick={toggleTheme}>
          {theme === 'light' ? <LightIcon /> : <DarkIcon />}
        </IconButton>
      </header>
      {!videoStream ? (
        <p>{I18n.t('label:loading')}</p>
      ) : (
        <>
          <div className="video-wrap">
            <Video stream={videoStream} muted={true} pipEnabled={false} />
          </div>
          {camSwitchAvailable && (
            <div className={classNames(classes.row, classes.optionRight)}>
              <Typography variant="overline" component="div">
                {I18n.t('devices:camera')}
              </Typography>
              <Button
                variant="outlined"
                onClick={switchCamera}
                disabled={!camSwitchAvailable}
                startIcon={<SwitchVideoIcon />}
              >
                {I18n.t('label:switch')}
              </Button>
            </div>
          )}
          <div className={classes.row}>
            <Typography variant="overline" component="div">
              {I18n.t('label:zoom')}
            </Typography>
            <div className={classes.zoom}>
              <StyledSlider
                value={zoomValue}
                min={1}
                max={4}
                step={0.1}
                onChange={updateZoom}
                disabled={joining}
              />
              <span className="zoom-value">{zoomValue}&times;</span>
            </div>
          </div>
        </>
      )}
      <p className="bottom">
        <Button
          fullWidth
          color="secondary"
          variant="contained"
          onClick={start}
          disabled={!videoStream || joining}
        >
          {joining && (
            <CircularProgress size="1rem" className={classes.loading} />
          )}
          {I18n.t('label:join_room')}
        </Button>
      </p>
    </div>
  );
};

const MainView = ({
  exitMeeting,
  localStream,
  theme,
  toggleTheme,
  isPresentationLayout,
  setPresentationLayout,
  resetLayout,
  options,
}) => {
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const classes = useStyles();

  const onExit = () => {
    setExitDialogOpen(true);
  };

  const handleExitCancel = () => {
    setExitDialogOpen(false);
  };

  const handleExitContinue = () => {
    setExitDialogOpen(false);
    exitMeeting();
  };

  const handlePresentationLayoutChange = () => {
    if (isPresentationLayout) {
      resetLayout();
    } else {
      setPresentationLayout();
    }
  };

  return (
    <div className="main">
      <header className={classes.optionRight}>
        <Typography variant="h5">{I18n.t('label:streaming')}</Typography>
        <IconButton onClick={toggleTheme}>
          {theme === 'light' ? <LightIcon /> : <DarkIcon />}
        </IconButton>
      </header>
      <div className="video-wrap">
        <Video stream={localStream} muted={true} pipEnabled={false} />
      </div>
      {options.layout_available && (
        <p>
          <FormControlLabel
            label={I18n.t('action:presentation_mode')}
            control={
              <Switch
                checked={isPresentationLayout}
                onChange={handlePresentationLayoutChange}
                name="togglePresentationMode"
                color="secondary"
              />
            }
          />
        </p>
      )}
      <p className="bottom">
        <StopButton fullWidth onClick={onExit} variant="outlined">
          {I18n.t('label:stop')}
        </StopButton>
      </p>
      <Dialog
        open={exitDialogOpen}
        onClose={handleExitCancel}
        aria-labelledby="exit-dialog-title"
        aria-describedby="exit-dialog-description"
      >
        <DialogTitle id="exit-dialog-title">
          {I18n.t('external-cam:stop-dialog:title')}
          <IconButton
            aria-label={I18n.t('dialog:close', {
              defaultValue: 'Close dialog window',
            })}
            className={classes.closeButton}
            onClick={handleExitCancel}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="exit-dialog-description">
            {I18n.t('external-cam:stop-dialog:description')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <GenericButton onClick={handleExitCancel} type="secondary">
            {I18n.t('label:cancel')}
          </GenericButton>
          <GenericButton onClick={handleExitContinue} autoFocus>
            {I18n.t('label:stop')}
          </GenericButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const Title = () => {
  const classes = useStyles();
  return (
    <Typography variant="h4" className={classes.title}>
      {I18n.t('label:external-camera')}
    </Typography>
  );
};

const WebViewDialog = ({ onClose, notAgain }) => {
  const classes = useStyles();
  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby="webview-dialog-title"
      aria-describedby="webview-dialog-description"
    >
      <DialogTitle id="webview-dialog-title">
        <WarningIcon className={classes.warningIcon} />
        {I18n.t('webview:dialog:header')}
        <IconButton
          aria-label={I18n.t('dialog:close', {
            defaultValue: 'Close dialog window',
          })}
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="webview-dialog-description">
          {I18n.t('webview:dialog:description')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <GenericButton onClick={notAgain} variant="outlined">
          {I18n.t('label:dont_show_again')}
        </GenericButton>
        <GenericButton onClick={onClose} autoFocus>
          {I18n.t('dialog:primaryLabel')}
        </GenericButton>
      </DialogActions>
    </Dialog>
  );
};

class ExternalCamera extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      init: true,
      ready: false,
      error: null,
      token: null,
      access_key: null,
      widescreen: false,
      shutdown: false,
      localStream: null,
      dialog: null,
      theme: props.theme,
      currentLayout: null,
      previousLayout: null,
      isPresentationLayout: false,
      webview: false,
      options: { layout_available: true },
    };
    this.webViewDetection = new WebViewDetection();
    this.activityMonitor = new ActivityMonitor(props.config.times.room);
    this.inactivityContext = {
      activityMonitor: this.activityMonitor,
      onEvent: () => {
        this.setState({ inactive: false });
        this.resetActivityMonitor();
      },
      onInactivity: () => {
        this.handleExitMeeting();
      },
    };
  }

  async componentDidMount() {
    const { token, config } = this.props;
    if (!token || token === 'missing-token') {
      this.setState({ error: 'error:page:msg:access_denied' });
      return;
    }
    try {
      const data = await fetchMeetingInfo(token, config);
      config.setLocale(data.options);
      this.userId = data.userId;
      if (data.options.widescreen) {
        this.setState({ widescreen: true });
      }
      if (!data.guest_token) {
        this.setState({ error: 'error:page:msg:access_denied' });
        return;
      }
      if (WebViewDetection.isSupported) {
        this.webViewDetection.init(() => {
          this.setState({ webview: true });
        });
      }
      const { access_key } = await registerGuest(data.guest_token, config, {
        name: `[Camera] ${data.name}`,
      });
      this.activityMonitor.onInactivity(() => {
        this.setState({ inactive: true });
      });
      this.activityMonitor.start();
      this.setState({ access_key }, () => this.connect());
      // this.setState({ access_key: token }, () => this.connect());
    } catch (error) {
      this.setState({ error: 'error:page:msg:access_denied' });
    }
  }

  connect = () => {
    eyeson.onEvent(this.handleEyesonEvent);
    eyeson.connect(this.state.access_key);
  };

  resetActivityMonitor = () => {
    this.activityMonitor.reset();
  };

  handleEyesonEvent = (event) => {
    const { type } = event;
    this.resetActivityMonitor();
    if (type === 'room_ready') {
      if (this.state.init) {
        if (!eyeson.user.apiId) {
          eyeson.user.apiId = event.content.user.id;
        }
        this.setState({
          init: false,
          ready: true,
          options: event.content.options,
          currentLayout: parseLayout(event.content.options),
          isPresentationLayout: checkIsPresentationLayout(
            event.content.options,
            this.userId
          ),
        });
      }
    } else if (type === 'accept') {
      const update = { ready: false };
      if (event.localStream) {
        update.localStream = event.localStream;
      }
      this.setState(update);
    } else if (type === 'stream_update') {
      if (event.localStream) {
        this.setState({ localStream: event.localStream });
      }
    } else if (type === 'options_update') {
      this.setState({
        options: event.options,
        currentLayout: parseLayout(event.options),
        isPresentationLayout: checkIsPresentationLayout(
          event.options,
          this.userId
        ),
      });
    } else if (type === 'warning') {
      this.showWarning(`error:page:msg:${event.name}`);
    } else if (type === 'error') {
      this.showWarning(`error:page:msg:${event.name}`);
      this.handleExitMeeting();
    } else if (type === 'exit') {
      this.exitMeeting();
    }
  };

  joinMeeting = (options) => {
    eyeson.join(options);
  };

  exitMeeting = () => {
    eyeson.destroy();
    this.cleanup();
  };

  handleExitMeeting = () => {
    this.resetLayout();
    this.exitMeeting();
  };

  setPresentationLayout = () => {
    if (this.state.options.layout_available) {
      this.setState({ previousLayout: this.state.currentLayout });
      eyeson.send({
        type: 'set_layout',
        params: {
          layout: 'auto',
          name: 'present-upper-6',
          users: [eyeson.user.apiId, this.userId, '', '', '', '', ''],
          voice_activation: true,
          show_names: false,
        },
      });
    }
  };

  resetLayout = () => {
    if (this.state.options.layout_available && this.state.previousLayout) {
      eyeson.send({ type: 'set_layout', params: this.state.previousLayout });
      this.setState({ previousLayout: null });
    }
  };

  cleanup = () => {
    this.activityMonitor.stop();
    this.setState({
      shutdown: true,
      init: false,
      ready: false,
      localStream: null,
      currentLayout: null,
      previousLayout: null,
      isPresentationLayout: false,
      options: { layout_available: true },
    });
  };

  showWarning = (message) => {
    this.props.enqueueSnackbar(I18n.t(message), {
      variant: 'warning',
      autoHideDuration: 5 * 1000,
    });
  };

  toggleTheme = () => {
    let newTheme = this.state.theme === 'dark' ? 'light' : 'dark';
    this.props.toggleTheme({ color: newTheme });
    this.setState({ theme: newTheme });
  };

  renderContent() {
    const { error, init, ready, shutdown, theme } = this.state;
    return [
      {
        condition: () => error !== null,
        component: () => (
          <div className="main">
            <h1>{I18n.t('error:page:header:unsupported')}</h1>
            <Typography>{I18n.t('error:page:msg:description')}</Typography>
            <Typography>{I18n.t(error)}</Typography>
          </div>
        ),
      },
      {
        condition: () => init,
        component: () => <Typography>{I18n.t('label:loading')}</Typography>,
      },
      {
        condition: () => ready,
        component: () =>
          this.props.isMobile ? (
            <ExternalCameraPreviewMobile
              join={this.joinMeeting}
              theme={theme}
              toggleTheme={this.toggleTheme}
              showWarning={this.showWarning}
            />
          ) : (
            <ExternalCameraPreview
              join={this.joinMeeting}
              theme={theme}
              toggleTheme={this.toggleTheme}
              showWarning={this.showWarning}
            />
          ),
      },
      {
        condition: () => shutdown,
        component: () => (
          <div className="main">
            <Typography variant="body1">
              {I18n.t('message:you_left_room')}
            </Typography>
            <p className="bottom">
              <Button
                fullWidth
                onClick={() => window.location.reload()}
                variant="outlined"
                color="secondary"
                startIcon={<ReloadIcon />}
              >
                {I18n.t('btn:start_over')}
              </Button>
            </p>
          </div>
        ),
      },
      {
        condition: () => true,
        component: () => (
          <MainView
            exitMeeting={this.handleExitMeeting}
            localStream={this.state.localStream}
            showWarning={this.showWarning}
            theme={theme}
            toggleTheme={this.toggleTheme}
            isPresentationLayout={this.state.isPresentationLayout}
            setPresentationLayout={this.setPresentationLayout}
            resetLayout={this.resetLayout}
            options={this.state.options}
          />
        ),
      },
    ]
      .find((component) => component.condition())
      .component();
  }

  render() {
    return (
      <div
        className={classNames('eyeson-external-camera', {
          mobile: this.props.isMobile,
        })}
      >
        <div className="wrapper">
          <Title />
          {this.renderContent()}
          {this.state.webview && (
            <WebViewDialog
              onClose={() => {
                this.setState({ webview: false });
              }}
              notAgain={() => {
                this.webViewDetection.cancel();
                this.setState({ webview: false });
              }}
            />
          )}
          {this.state.inactive && (
            <DialogWindow
              id="inactive-dialog"
              title={I18n.t('dialog:title:inactive')}
              onClose={this.inactivityContext.onEvent}
              role="alertdialog"
              useAriaDescription
            >
              <InactiveDialog
                dialogId="inactive-dialog"
                context={this.inactivityContext}
                countDownDialogTime={this.props.config.times.dialog}
              />
            </DialogWindow>
          )}
        </div>
      </div>
    );
  }
}

export default withSnackbar(ExternalCamera);
